'use client'

import React, { memo, useState } from 'react'

import { useRouter } from 'next/navigation'

import {
  ArrowForward,
  Campaign,
  Circle,
  EmojiEvents,
  Engineering,
  RemoveRedEye,
  School,
  ThumbUp,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { format } from 'date-fns'

interface InternalNewsProps {
  mounted: boolean
}

const internalNews = [
  {
    id: 1,
    category: 'Achievement',
    title: 'Historic Milestone: 10 Million Digital IDs Issued',
    excerpt:
      'Ethiopia reaches a landmark achievement with over 10 million citizens now enrolled in the national digital identity system.',
    image: '/assets/images/fayda_id.jpg',
    author: {
      name: 'Dr. Abebe Tadesse',
      role: 'Director General',
    },
    department: 'Executive Office',
    publishedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    engagement: { views: 3847, likes: 342 },
  },
  {
    id: 2,
    category: 'Technology',
    title: 'Next-Gen Biometric Scanners Deployment Begins',
    excerpt:
      'Revolutionary biometric scanning technology with 99.99% accuracy rate starts rolling out nationwide.',
    image: '/assets/images/ethpass_avatar.png',
    author: {
      name: 'Eng. Sara Bekele',
      role: 'Chief Technology Officer',
    },
    department: 'Technology Division',
    publishedAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
    engagement: { views: 2156, likes: 178 },
  },
  {
    id: 3,
    category: 'Training',
    title: 'Advanced Security Certification Program Launch',
    excerpt:
      'New comprehensive security training program now available for all system administrators.',
    image: '/assets/images/fayda_id.jpg',
    author: {
      name: 'Col. Dawit Haile',
      role: 'Security Director',
    },
    department: 'Security Operations',
    publishedAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
    engagement: { views: 1523, likes: 98 },
  },
]

const InternalNews = memo<InternalNewsProps>(({ mounted }) => {
  const theme = useTheme()
  const router = useRouter()
  const [newsFilter, setNewsFilter] = useState('all')

  const filteredNews =
    newsFilter === 'all'
      ? internalNews
      : internalNews.filter((news) => news.category.toLowerCase() === newsFilter)

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Achievement':
        return <EmojiEvents />
      case 'Technology':
        return <Engineering />
      case 'Training':
        return <School />
      default:
        return <Campaign />
    }
  }

  const getCategoryColor = (category: string): any => {
    switch (category) {
      case 'Achievement':
        return 'success'
      case 'Technology':
        return 'info'
      case 'Training':
        return 'primary'
      default:
        return 'default'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Grid container spacing={6}>
        {/* Left Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box position="sticky" top={100}>
            <Chip
              icon={<Campaign />}
              label="INTERNAL COMMUNICATIONS"
              color="primary"
              variant="outlined"
              sx={{ mb: 3, fontWeight: 600 }}
            />
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Latest Updates
            </Typography>
            <Typography variant="h6" color="text.secondary" mb={4}>
              Stay informed with the latest news, achievements, and announcements.
            </Typography>

            <ToggleButtonGroup
              value={newsFilter}
              exclusive
              onChange={(_e, value) => value && setNewsFilter(value)}
              sx={{ mb: 4 }}
              fullWidth
            >
              <ToggleButton value="all" sx={{ px: 2 }}>
                All
              </ToggleButton>
              <ToggleButton value="achievement">Achievements</ToggleButton>
              <ToggleButton value="technology">Technology</ToggleButton>
              <ToggleButton value="training">Training</ToggleButton>
            </ToggleButtonGroup>

            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Circle sx={{ fontSize: 10, color: theme.palette.success.main }} />
                <Typography variant="body2">
                  <strong>3,847</strong> staff members online
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Circle sx={{ fontSize: 10, color: theme.palette.info.main }} />
                <Typography variant="body2">
                  <strong>24</strong> new announcements this week
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Circle sx={{ fontSize: 10, color: theme.palette.warning.main }} />
                <Typography variant="body2">
                  <strong>5</strong> upcoming training sessions
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Grid>

        {/* News Articles */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            {filteredNews.map((news, index) => (
              <Fade key={news.id} in={mounted} timeout={600 + index * 100}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <Grid container>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Chip
                              icon={getCategoryIcon(news.category)}
                              label={news.category}
                              size="small"
                              color={getCategoryColor(news.category)}
                              sx={{ fontWeight: 600 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {format(news.publishedAt, 'MMM dd, yyyy')}
                            </Typography>
                          </Stack>

                          <Typography variant="h6" fontWeight={700}>
                            {news.title}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {news.excerpt}
                          </Typography>

                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                              sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main }}
                            >
                              {news.author.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {news.author.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {news.author.role} • {news.department}
                              </Typography>
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                              <Stack direction="row" spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <RemoveRedEye fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {news.engagement.views.toLocaleString()}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <ThumbUp fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {news.engagement.likes}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box
                        sx={{
                          height: '100%',
                          minHeight: 200,
                          backgroundImage: `url(${news.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Fade>
            ))}
          </Stack>

          <Box textAlign="center" mt={4}>
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => router.push('/announcements')}
              sx={{
                fontWeight: 600,
                borderWidth: 1.5,
                '&:hover': {
                  borderWidth: 1.5,
                  borderColor: theme.palette.primary.dark,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '& .MuiButton-endIcon': {
                    transform: 'translateX(4px)',
                  },
                },
              }}
            >
              View All Announcements
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
})

InternalNews.displayName = 'InternalNews'

export default InternalNews
